import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTimes,
  faBolt,
  faBookmark,
  faCheck,
  faCode,
  faTag,
  faCompass,
} from '@fortawesome/free-solid-svg-icons';
import { BUILTIN_PRESETS, PRESET_CATEGORIES } from '../presetStrategies';

const CATEGORY_COLORS = {
  Breakouts: { bg: '#fff4e6', color: '#d97706', border: '#fed7aa' },
  Reversals: { bg: '#f3e8ff', color: '#7c3aed', border: '#e9d5ff' },
  Momentum: { bg: '#e0f2fe', color: '#0284c7', border: '#bae6fd' },
  Delivery: { bg: '#ecfdf5', color: '#059669', border: '#a7f3d0' },
  Volatility: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  Patterns: { bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
  Derivatives: { bg: '#f0fdfa', color: '#0d9488', border: '#99f6e4' },
  Fundamentals: { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
  Saved: { bg: '#fdf2f8', color: '#db2777', border: '#fbcfe8' },
};

const PresetsModal = ({
  isOpen,
  onClose,
  onSelectPreset,
  savedScans = [],
  currentQuery = '',
}) => {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Reset category/search when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  const categoryCounts = useMemo(() => {
    const counts = { ALL: BUILTIN_PRESETS.length, SAVED: savedScans.length };
    PRESET_CATEGORIES.forEach((cat) => {
      counts[cat] = BUILTIN_PRESETS.filter((p) => p.category === cat).length;
    });
    return counts;
  }, [savedScans]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();

    // 1. If 'SAVED' category selected
    if (selectedCategory === 'SAVED') {
      return savedScans
        .map((s) => ({
          id: `saved_${s.id}`,
          isSaved: true,
          name: s.name,
          category: 'Saved',
          universe: s.universe,
          blurb: 'Custom saved strategy in your workspace',
          query: s.source,
        }))
        .filter((item) => {
          if (!query) return true;
          return (
            item.name?.toLowerCase().includes(query) ||
            item.query?.toLowerCase().includes(query)
          );
        });
    }

    // 2. Built-in presets
    let presets = BUILTIN_PRESETS;
    if (selectedCategory !== 'ALL') {
      presets = presets.filter((p) => p.category === selectedCategory);
    }

    let items = presets.map((p) => ({
      id: `preset_${p.slug}`,
      isSaved: false,
      name: p.name,
      category: p.category,
      universe: p.universe,
      blurb: p.blurb,
      query: p.query,
    }));

    // If 'ALL' and query is present, also search in saved scans
    if (selectedCategory === 'ALL' && query) {
      const matchedSaved = savedScans
        .filter(
          (s) =>
            s.name?.toLowerCase().includes(query) ||
            s.source?.toLowerCase().includes(query)
        )
        .map((s) => ({
          id: `saved_${s.id}`,
          isSaved: true,
          name: s.name,
          category: 'Saved',
          universe: s.universe,
          blurb: 'Custom saved strategy',
          query: s.source,
        }));
      items = [...items, ...matchedSaved];
    }

    if (!query) return items;

    return items.filter((p) => {
      return (
        p.name?.toLowerCase().includes(query) ||
        p.blurb?.toLowerCase().includes(query) ||
        p.query?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    });
  }, [search, selectedCategory, savedScans]);

  if (!isOpen) return null;

  const normCurrentQuery = (currentQuery || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1070 }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
        style={{ maxWidth: '1100px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content shadow-lg border-0"
          style={{ maxHeight: '88vh', borderRadius: '8px' }}
        >
          {/* Modal Header */}
          <div className="modal-header py-2 px-3 bg-white border-bottom d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded text-white"
                style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#1652f0',
                }}
              >
                <FontAwesomeIcon icon={faBolt} />
              </span>
              <div>
                <h6 className="modal-title font-weight-bold mb-0 text-dark">
                  Strategy & Presets Library
                </h6>
                <small className="text-muted" style={{ fontSize: '11px' }}>
                  142 built-in quantitative scanners from PatternsRadar + saved strategies
                </small>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-sm btn-light text-muted p-1 px-2"
              onClick={onClose}
              title="Close (Esc)"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Search Bar & Filter Header */}
          <div className="px-3 pt-3 pb-2 bg-light border-bottom">
            {/* Live Search Input */}
            <div className="position-relative mb-2">
              <FontAwesomeIcon
                icon={faSearch}
                className="position-absolute text-muted"
                style={{ left: '12px', top: '10px', fontSize: '13px' }}
              />
              <input
                type="text"
                className="form-control form-control-sm bg-white ps-5 pe-5 py-2"
                style={{
                  fontSize: '13px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                }}
                placeholder="Search strategies by name, indicator (e.g. RSI, EMA, 52-week, Delivery), or formula syntax..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
              {search && (
                <button
                  type="button"
                  className="btn btn-link btn-xs position-absolute text-muted p-0 text-decoration-none"
                  style={{ right: '12px', top: '8px' }}
                  onClick={() => setSearch('')}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>

            {/* Category Tabs / Pills */}
            <div
              className="d-flex align-items-center gap-1 overflow-auto pb-1"
              style={{ whiteSpace: 'nowrap' }}
            >
              <button
                type="button"
                className={`btn btn-xs px-2 py-1 rounded-pill ${
                  selectedCategory === 'ALL'
                    ? 'btn-primary text-white font-weight-bold'
                    : 'btn-outline-secondary bg-white text-dark'
                }`}
                style={{ fontSize: '11px' }}
                onClick={() => setSelectedCategory('ALL')}
              >
                All Presets ({categoryCounts.ALL})
              </button>

              {PRESET_CATEGORIES.map((cat) => {
                const count = categoryCounts[cat] || 0;
                const isSelected = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    className={`btn btn-xs px-2 py-1 rounded-pill ${
                      isSelected
                        ? 'btn-primary text-white font-weight-bold'
                        : 'btn-outline-secondary bg-white text-dark'
                    }`}
                    style={{ fontSize: '11px' }}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat} ({count})
                  </button>
                );
              })}

              {savedScans.length > 0 && (
                <button
                  type="button"
                  className={`btn btn-xs px-2 py-1 rounded-pill ${
                    selectedCategory === 'SAVED'
                      ? 'btn-success text-white font-weight-bold'
                      : 'btn-outline-success bg-white'
                  }`}
                  style={{ fontSize: '11px' }}
                  onClick={() => setSelectedCategory('SAVED')}
                >
                  <FontAwesomeIcon icon={faBookmark} className="me-1" />
                  My Saved ({savedScans.length})
                </button>
              )}
            </div>
          </div>

          {/* Cards Body */}
          <div
            className="modal-body p-3 bg-white"
            style={{ minHeight: '380px', overflowY: 'auto' }}
          >
            {filteredItems.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <FontAwesomeIcon icon={faCompass} size="2x" className="mb-2 text-secondary" />
                <p className="mb-1 font-weight-bold">No strategies found matching "{search}"</p>
                <small>Try searching with another keyword or select "All Presets".</small>
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '12px',
                }}
              >
                {filteredItems.map((item) => {
                  const itemNormQuery = (item.query || '')
                    .trim()
                    .replace(/\s+/g, ' ')
                    .toLowerCase();
                  const isActive =
                    Boolean(normCurrentQuery) &&
                    normCurrentQuery === itemNormQuery;

                  const colorScheme =
                    CATEGORY_COLORS[item.category] || CATEGORY_COLORS.Breakouts;

                  return (
                    <div
                      key={item.id}
                      className={`card h-100 border transition-all ${
                        isActive ? 'border-primary shadow-sm bg-light' : 'bg-white'
                      }`}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '6px',
                        transition: 'all 0.15s ease',
                      }}
                      onClick={() => {
                        onSelectPreset(item);
                        onClose();
                      }}
                    >
                      <div className="card-body p-3 d-flex flex-column justify-content-between">
                        <div>
                          {/* Top Tag & Universe */}
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span
                              className="badge px-2 py-0 border font-weight-bold"
                              style={{
                                fontSize: '10px',
                                backgroundColor: colorScheme.bg,
                                color: colorScheme.color,
                                borderColor: colorScheme.border,
                              }}
                            >
                              <FontAwesomeIcon icon={faTag} className="me-1" />
                              {item.category}
                            </span>

                            <div className="d-flex align-items-center gap-1">
                              {item.universe && (
                                <span
                                  className="badge bg-light text-muted border"
                                  style={{ fontSize: '10px' }}
                                >
                                  Top {item.universe}
                                </span>
                              )}
                              {isActive && (
                                <span
                                  className="badge bg-primary text-white"
                                  style={{ fontSize: '10px' }}
                                >
                                  <FontAwesomeIcon icon={faCheck} className="me-1" />
                                  Active
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Title */}
                          <h6
                            className="font-weight-bold mb-1 text-dark text-capitalize"
                            style={{ fontSize: '13px', lineHeight: '1.3' }}
                          >
                            {item.name}
                          </h6>

                          {/* Blurb */}
                          <p
                            className="text-muted small mb-2"
                            style={{
                              fontSize: '11px',
                              lineHeight: '1.4',
                              minHeight: '30px',
                            }}
                          >
                            {item.blurb}
                          </p>
                        </div>

                        {/* Sift Syntax Box */}
                        <div className="mt-auto">
                          <div
                            className="p-1 px-2 rounded font-monospace small bg-light border text-truncate"
                            style={{
                              fontSize: '10.5px',
                              color: '#334155',
                              backgroundColor: '#f8fafc',
                            }}
                            title={item.query}
                          >
                            <FontAwesomeIcon
                              icon={faCode}
                              className="text-muted me-1"
                              style={{ fontSize: '10px' }}
                            />
                            {item.query}
                          </div>

                          <div className="d-flex justify-content-end mt-2">
                            <button
                              type="button"
                              className={`btn btn-xs ${
                                isActive ? 'btn-primary' : 'btn-outline-primary'
                              }`}
                              style={{ fontSize: '11px', padding: '2px 8px' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelectPreset(item);
                                onClose();
                              }}
                            >
                              {isActive ? 'Loaded in Studio' : 'Apply Strategy'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="modal-footer py-2 px-3 bg-light border-top d-flex justify-content-between align-items-center">
            <small className="text-muted" style={{ fontSize: '11px' }}>
              Showing {filteredItems.length} of {categoryCounts.ALL + savedScans.length} strategies
            </small>
            <button
              type="button"
              className="btn btn-default btn-xs"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresetsModal;
